import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const COMPANY = {
  name: 'Fabritech',
  tagline: 'Training Center',
  address: 'YYUSSA Plaza, Kisimenti — Kigali, Rwanda',
  email: 'info@fabritech.rw',
  phone: '0788601280',
  website: 'www.fabritech.rw',
};

const FONT = 'helvetica';

const INK = {
  black: [33, 37, 41],
  mid: [107, 114, 128],
  light: [156, 163, 175],
  line: [229, 231, 235],
  headerBg: [55, 65, 81],
  white: [255, 255, 255],
  panel: [249, 250, 251],
};

const loadCompressedLogo = async (url) => {
  const image = new Image();
  image.src = url;
  await new Promise((resolve, reject) => {
    image.onload = resolve;
    image.onerror = reject;
  });

  const maxDimension = 160;
  const scale = Math.min(1, maxDimension / Math.max(image.naturalWidth, image.naturalHeight));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
  const context = canvas.getContext('2d');
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/jpeg', 0.7);
};

const setInk = (doc, color) => doc.setTextColor(...color);
const setDrawInk = (doc, color) => doc.setDrawColor(...color);

/**
 * @param {Object} options
 * @param {string} options.title - Report title line
 * @param {Array<{label: string, value: string}>} options.meta - Cohort, field, date, summary, etc.
 * @param {string[][]} options.head - Table header row(s)
 * @param {string[][]} options.body - Table body rows
 * @param {string} options.generatedBy - Footer attribution
 * @param {string} options.filename - Download filename
 * @param {boolean} [options.landscape]
 */
export async function generateAttendanceReportPdf({
  title,
  meta = [],
  head,
  body,
  generatedBy,
  filename,
  landscape = false,
}) {
  const doc = new jsPDF({
    orientation: landscape ? 'landscape' : 'portrait',
    unit: 'mm',
    format: 'a4',
    compress: true,
    putOnlyUsedFonts: true,
    precision: 2,
  });
  const margin = 14;
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  let logoData = null;
  try {
    logoData = await loadCompressedLogo(`${process.env.PUBLIC_URL || ''}/logo.png`);
  } catch {
    /* logo optional */
  }

  let y = margin;
  const headerTextX = logoData ? margin + 24 : margin;

  if (logoData) {
    doc.addImage(logoData, 'JPEG', margin, y - 1, 20, 20, undefined, 'FAST');
  }

  doc.setFont(FONT, 'bold');
  doc.setFontSize(15);
  setInk(doc, INK.black);
  doc.text(`${COMPANY.name} ${COMPANY.tagline}`, headerTextX, y + 5);

  doc.setFont(FONT, 'normal');
  doc.setFontSize(8);
  setInk(doc, INK.mid);
  doc.text(COMPANY.address, headerTextX, y + 10);
  doc.text(`${COMPANY.email}  ·  ${COMPANY.phone}  ·  ${COMPANY.website}`, headerTextX, y + 14.5);

  y += 24;
  setDrawInk(doc, INK.line);
  doc.setLineWidth(0.35);
  doc.line(margin, y, pageW - margin, y);
  y += 9;

  doc.setFont(FONT, 'bold');
  doc.setFontSize(12);
  setInk(doc, INK.black);
  doc.text(title || 'Attendance Report', margin, y);
  y += 7;

  if (meta.length > 0) {
    const panelX = margin;
    const panelW = pageW - margin * 2;
    const panelH = meta.length * 5.5 + 6;
    doc.setFillColor(...INK.panel);
    setDrawInk(doc, INK.line);
    doc.setLineWidth(0.2);
    doc.roundedRect(panelX, y - 3, panelW, panelH, 2, 2, 'FD');

    let metaY = y + 1;
    meta.forEach(({ label, value }) => {
      doc.setFont(FONT, 'bold');
      doc.setFontSize(8.5);
      setInk(doc, INK.mid);
      doc.text(`${label}`, panelX + 4, metaY);
      doc.setFont(FONT, 'normal');
      setInk(doc, INK.black);
      const valueX = panelX + 34;
      const lines = doc.splitTextToSize(String(value ?? '—'), panelW - 40);
      doc.text(lines, valueX, metaY);
      metaY += Math.max(5, lines.length * 4.2);
    });
    y += panelH + 4;
  }

  autoTable(doc, {
    startY: y,
    head,
    body,
    theme: 'plain',
    styles: {
      font: FONT,
      fontSize: 9,
      cellPadding: { top: 3.5, right: 4, bottom: 3.5, left: 4 },
      textColor: INK.black,
      lineColor: INK.line,
      lineWidth: 0.2,
      valign: 'middle',
    },
    headStyles: {
      fillColor: INK.headerBg,
      textColor: INK.white,
      fontStyle: 'bold',
      halign: 'left',
    },
    alternateRowStyles: { fillColor: [252, 252, 252] },
    columnStyles: head?.[0]?.[0] === '#'
      ? { 0: { cellWidth: 12, halign: 'center' } }
      : {},
    margin: { left: margin, right: margin, bottom: 16 },
    didDrawPage: () => {
      const footerY = pageH - 8;
      doc.setFont(FONT, 'normal');
      doc.setFontSize(7.5);
      setInk(doc, INK.mid);
      if (generatedBy) {
        doc.text(generatedBy, margin, footerY);
      }
      doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageW - margin, footerY, { align: 'right' });
    },
  });

  doc.save(filename);
}

/** Build consistent Excel rows for attendance exports */
export function buildAttendanceExcelRows({
  title,
  meta = [],
  head,
  body,
  generatedBy,
}) {
  const rows = [
    [`${COMPANY.name} ${COMPANY.tagline}`],
    [COMPANY.address],
    [`${COMPANY.email}  |  ${COMPANY.phone}`],
    [],
    [title || 'Attendance Report'],
    [],
  ];
  meta.forEach(({ label, value }) => {
    rows.push([`${label}:`, String(value ?? '—')]);
  });
  rows.push([], head, ...body, [], [generatedBy || '']);
  return rows;
}

export { COMPANY as ATTENDANCE_REPORT_COMPANY };
